/**
 * FloatingWaWidget — Iter Feb 2026 · R66.
 *
 * Draggable, 8-way resizable floating WhatsApp mini-inbox. Persists open
 * / collapsed / compact / size / position in localStorage per user.
 *
 * R66 upgrades (over R44/R45):
 *   • Drag uses a hidden left/top model (computed from viewport) so the
 *     grab feel is 1:1 with the mouse — old model drifted when the panel
 *     was near the left edge.
 *   • 8 resize handles (N · S · E · W · NE · NW · SE · SW) — the widget
 *     can now be reshaped from any side or corner.
 *   • Compact mode (toggle in header + auto-trigger when width < 480):
 *     WaInbox switches to icon-only conversation rail (56px wide) with a
 *     hover tooltip showing the person's name. Perfect for keeping the
 *     widget a tiny tower on the side while you work on other pages.
 *
 * Mounted globally in AppShell — routes never re-mount it, so a chat
 * session survives page changes.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import WaInbox from "@/pages/app/WaInbox";
import { MessageCircle, X, Minus, Maximize2, PanelLeftClose, PanelLeftOpen } from "lucide-react";

const LS_KEY = "acq_floating_wa_v3";

// Wider default so the 2-pane WA inbox is usable out of the box.
const DEFAULT = {
  open: false,
  collapsed: false,
  forceCompact: false,       // when true → always compact regardless of size
  width: 720,
  height: 620,
  left: null,                // computed from right/bottom on first mount
  top: null,
};

// Minimums — compact mode allows narrower panels (avatar rail + thread pane).
const MIN_W_COMPACT = 320;
const MIN_W_FULL = 480;
const MIN_H = 360;
// Width below which we auto-force compact mode. Matches the 2-pane
// threshold in WaInbox — narrower than this and the 320px thread list
// would eat everything.
const AUTO_COMPACT_BELOW = 480;

function readState() {
  try {
    const s = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    return { ...DEFAULT, ...s,
             open: Boolean(s.open),
             collapsed: Boolean(s.collapsed),
             forceCompact: Boolean(s.forceCompact) };
  } catch { return { ...DEFAULT }; }
}

/** Public API — anyone (e.g. WhatsAppHeaderButton) can dispatch these
 * DOM events to control the widget without a shared context. */
export function openFloatingWa()   { window.dispatchEvent(new CustomEvent("acq:wa-widget-open")); }
export function closeFloatingWa()  { window.dispatchEvent(new CustomEvent("acq:wa-widget-close")); }
export function toggleFloatingWa() { window.dispatchEvent(new CustomEvent("acq:wa-widget-toggle")); }

/** Clamp geometry to viewport. When left/top are null, seed them from
 * a bottom-right anchor (24px inset). */
function clampGeom({ width, height, left, top, forceCompact }) {
  const vw = window.innerWidth || 1200;
  const vh = window.innerHeight || 800;
  const minW = forceCompact ? MIN_W_COMPACT : MIN_W_FULL;
  const w = Math.max(minW, Math.min(width, vw - 20));
  const h = Math.max(MIN_H, Math.min(height, vh - 20));
  let l = left, t = top;
  if (l == null || t == null) {
    l = vw - w - 24;
    t = vh - h - 24;
  }
  l = Math.max(0, Math.min(l, vw - w));
  t = Math.max(0, Math.min(t, vh - h));
  return { width: w, height: h, left: l, top: t };
}

export default function FloatingWaWidget() {
  const [state, setState] = useState(readState);
  // active drag/resize op — { kind: 'drag'|'resize', edge?: 'n/s/e/w/ne/nw/se/sw', anchor: {...} }
  const [op, setOp] = useState(null);
  const panelRef = useRef(null);

  const persist = useCallback((patch) => setState(s => {
    const merged = { ...s, ...patch };
    const geo = clampGeom(merged);
    const next = { ...merged, ...geo };
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* noop */ }
    return next;
  }), []);

  // ── Public control events ──────────────────────────────────────────
  useEffect(() => {
    const onOpen = () => setState(s => ({ ...s, open: true, collapsed: false }));
    const onClose = () => setState(s => ({ ...s, open: false }));
    const onToggle = () => setState(s => ({ ...s, open: !s.open }));
    window.addEventListener("acq:wa-widget-open", onOpen);
    window.addEventListener("acq:wa-widget-close", onClose);
    window.addEventListener("acq:wa-widget-toggle", onToggle);
    return () => {
      window.removeEventListener("acq:wa-widget-open", onOpen);
      window.removeEventListener("acq:wa-widget-close", onClose);
      window.removeEventListener("acq:wa-widget-toggle", onToggle);
    };
  }, []);

  // Sync any state change to LS (esp. from public events which bypass persist)
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* noop */ }
  }, [state]);

  // ── ESC closes when open ───────────────────────────────────────────
  useEffect(() => {
    if (!state.open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const t = e.target;
      const isText = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (isText) return;
      setState(s => ({ ...s, open: false }));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [state.open]);

  // ── Clamp on viewport resize ──────────────────────────────────────
  useEffect(() => {
    const onResize = () => persist({});
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [persist]);

  // ── Drag / resize move loop (single effect for both) ──────────────
  useEffect(() => {
    if (!op) return undefined;
    const onMove = (e) => {
      if (op.kind === "drag") {
        // Move left/top so the grab-offset stays under the cursor.
        const nextLeft = e.clientX - op.anchor.grabX;
        const nextTop  = e.clientY - op.anchor.grabY;
        persist({ left: nextLeft, top: nextTop });
      } else if (op.kind === "resize") {
        const { edge, startX, startY, startW, startH, startL, startT } = op.anchor;
        let w = startW, h = startH, l = startL, t = startT;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (edge.includes("e")) w = startW + dx;
        if (edge.includes("s")) h = startH + dy;
        if (edge.includes("w")) { w = startW - dx; l = startL + dx; }
        if (edge.includes("n")) { h = startH - dy; t = startT + dy; }
        // Clamp min so left/top don't run away when we hit the wall.
        const minW = state.forceCompact ? MIN_W_COMPACT : MIN_W_FULL;
        if (w < minW) { if (edge.includes("w")) l = startL + (startW - minW); w = minW; }
        if (h < MIN_H) { if (edge.includes("n")) t = startT + (startH - MIN_H); h = MIN_H; }
        persist({ width: w, height: h, left: l, top: t });
      }
    };
    const onUp = () => setOp(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [op, persist, state.forceCompact]);

  // Compact = forced OR too narrow.
  const compact = state.forceCompact || state.width < AUTO_COMPACT_BELOW;

  const startDrag = (e) => {
    if (e.button !== 0) return;
    const t = e.target;
    if (t.closest?.("button")) return;   // don't hijack header buttons
    e.preventDefault();
    const rect = panelRef.current.getBoundingClientRect();
    setOp({ kind: "drag", anchor: {
      grabX: e.clientX - rect.left,
      grabY: e.clientY - rect.top,
    }});
  };

  const startResize = (edge) => (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setOp({ kind: "resize", anchor: {
      edge,
      startX: e.clientX, startY: e.clientY,
      startW: state.width, startH: state.height,
      startL: state.left, startT: state.top,
    }});
  };

  // Passed to WaInbox — its 2-pane grid collapses to a 56px avatar rail.
  const embeddedProps = useMemo(() => ({ embedded: true, compact }), [compact]);

  // Closed → render nothing (so no ghost element blocks header clicks).
  if (!state.open) return null;

  // Collapsed → tiny bar bottom-right (anchored to top-left of previous geom)
  if (state.collapsed) {
    return (
      <div className="fixed z-[60] bg-background border shadow-xl flex items-center gap-2 px-3 py-1.5"
             style={{ left: state.left, top: state.top }}
             data-testid="floating-wa-collapsed">
        <MessageCircle className="w-4 h-4 text-emerald-600" />
        <span className="text-xs font-semibold">WhatsApp Inbox</span>
        <button onClick={() => persist({ collapsed: false })} className="p-1 hover:bg-neutral-100" title="Expand" data-testid="floating-wa-expand">
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => persist({ open: false })} className="p-1 hover:bg-neutral-100" title="Close" data-testid="floating-wa-close-collapsed">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // ── 8-way resize handles ───────────────────────────────────────────
  // Each handle is a thin invisible strip along its edge (or 12×12 at
  // the corner). Cursor hints so users know which axis they'll drag.
  const ResizeHandles = () => (
    <>
      <div className="absolute top-0 left-0 right-0 h-1 cursor-ns-resize z-20" onMouseDown={startResize("n")} data-testid="floating-wa-resize-n" />
      <div className="absolute bottom-0 left-0 right-0 h-1 cursor-ns-resize z-20" onMouseDown={startResize("s")} data-testid="floating-wa-resize-s" />
      <div className="absolute top-0 bottom-0 left-0 w-1 cursor-ew-resize z-20" onMouseDown={startResize("w")} data-testid="floating-wa-resize-w" />
      <div className="absolute top-0 bottom-0 right-0 w-1 cursor-ew-resize z-20" onMouseDown={startResize("e")} data-testid="floating-wa-resize-e" />
      <div className="absolute -top-1 -left-1 w-3 h-3 cursor-nwse-resize z-30" onMouseDown={startResize("nw")} data-testid="floating-wa-resize-nw" />
      <div className="absolute -top-1 -right-1 w-3 h-3 cursor-nesw-resize z-30" onMouseDown={startResize("ne")} data-testid="floating-wa-resize-ne" />
      <div className="absolute -bottom-1 -left-1 w-3 h-3 cursor-nesw-resize z-30" onMouseDown={startResize("sw")} data-testid="floating-wa-resize-sw" />
      <div className="absolute -bottom-1 -right-1 w-3 h-3 cursor-nwse-resize z-30" onMouseDown={startResize("se")} data-testid="floating-wa-resize-se" />
    </>
  );

  return (
    <div
      ref={panelRef}
      className="fixed z-[60] bg-background border shadow-2xl flex flex-col select-none"
      style={{ width: state.width, height: state.height, left: state.left, top: state.top }}
      data-testid="floating-wa-panel"
      data-compact={compact ? "true" : "false"}
    >
      <ResizeHandles />
      {/* Header (drag handle) */}
      <div
        className="border-b flex items-center gap-2 px-2 py-1.5 cursor-move bg-emerald-500 text-white select-none relative z-10"
        onMouseDown={startDrag}
        data-testid="floating-wa-header"
      >
        <MessageCircle className="w-4 h-4 shrink-0" />
        {!compact && <span className="text-xs font-semibold flex-1 pointer-events-none">WhatsApp Inbox</span>}
        {compact && <span className="flex-1 pointer-events-none" />}
        <button
          onClick={() => persist({ forceCompact: !state.forceCompact })}
          className="p-1 hover:bg-white/10"
          title={state.forceCompact ? "Exit compact mode" : "Compact mode (icon-only rail)"}
          data-testid="floating-wa-compact-toggle"
        >
          {state.forceCompact
            ? <PanelLeftOpen className="w-3.5 h-3.5" />
            : <PanelLeftClose className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => persist({ collapsed: true })}
          className="p-1 hover:bg-white/10"
          title="Collapse"
          data-testid="floating-wa-collapse"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => persist({ open: false })}
          className="p-1 hover:bg-white/10"
          title="Close (ESC)"
          data-testid="floating-wa-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-hidden min-h-0 relative">
        <WaInbox {...embeddedProps} />
      </div>
    </div>
  );
}
